import React, { createContext, useContext, useState } from 'react';

const GameObjectsContext = createContext();

export const useGameObjects = () => {
  const context = useContext(GameObjectsContext);
  if (!context) {
    throw new Error('useGameObjects must be used within GameObjectsProvider');
  }
  return context;
};

export const GameObjectsProvider = ({ children }) => {
  const [bombs, setBombs] = useState([]);
  const [explosions, setExplosions] = useState([]);
  const [walls, setWalls] = useState([]);
  const [upgrades, setUpgrades] = useState([]);

  // Game object management actions
  const actions = React.useMemo(() => ({
    setBombs,
    setExplosions,
    setWalls,
    setUpgrades,

    // Bomb utilities
    addBomb: (bomb) => {
      setBombs(prev => [...prev, bomb]);
    },

    removeBomb: (bombId) => {
      setBombs(prev => prev.filter(bomb => bomb.id !== bombId));
    },

    updateBomb: (bombId, updates) => {
      setBombs(prev => prev.map(bomb => 
        bomb.id === bombId ? { ...bomb, ...updates } : bomb
      ));
    },

    findBombAt: (gridX, gridY) => {
      return bombs.find(bomb => bomb.x === gridX && bomb.y === gridY);
    },

    // Explosion utilities
    addExplosions: (newExplosions) => {
      setExplosions(prev => [...prev, ...newExplosions]);
    },

    clearExplosions: () => {
      setExplosions([]);
    },

    // Wall utilities
    addWalls: (newWalls) => {
      setWalls(prev => [...prev, ...newWalls]);
    },

    removeWalls: (wallsToRemove) => {
      setWalls(prev => prev.filter(wall => 
        !wallsToRemove.some(toRemove => toRemove.x === wall.x && toRemove.y === wall.y)
      ));
    },

    updateWalls: (wallUpdates) => {
      setWalls(prev => prev.map(wall => {
        const update = wallUpdates.find(u => u.x === wall.x && u.y === wall.y);
        return update ? { ...wall, ...update } : wall;
      }));
    },

    isWallAt: (gridX, gridY) => {
      return walls.some(wall => wall.x === gridX && wall.y === gridY);
    },

    // Upgrade utilities
    addUpgrades: (newUpgrades) => {
      setUpgrades(prev => [...prev, ...newUpgrades]);
    },

    removeUpgrades: (upgradeIds) => {
      setUpgrades(prev => prev.filter(upgrade => 
        !upgradeIds.includes(upgrade.id)
      ));
    },

    updateUpgrades: (upgradeUpdates) => {
      setUpgrades(prev => prev.map(upgrade => {
        const update = upgradeUpdates.find(u => u.id === upgrade.id);
        return update ? { ...upgrade, ...update } : upgrade;
      }));
    },

    // Global reset
    resetGameObjects: () => {
      setBombs([]);
      setExplosions([]);
      setWalls([]);
      setUpgrades([]);
    }
  }), [bombs, walls]);

  // Memoized state values
  const state = React.useMemo(() => ({
    bombs,
    explosions,
    walls,
    upgrades
  }), [bombs, explosions, walls, upgrades]);

  const value = React.useMemo(() => ({
    ...state,
    ...actions
  }), [state, actions]);

  return (
    <GameObjectsContext.Provider value={value}>
      {children}
    </GameObjectsContext.Provider>
  );
};

export default GameObjectsContext;